import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import Anzali from "../assets/images/mostafa-yekrangi-d4uDkMbdma0-unsplash.webp";

interface Destination {
    name: string;
    subtitle: string;
    image: string;
    slug: string;
}

const destinations: Destination[] = [
    { name: "رشت", subtitle: "اجاره ویلا", slug: "شمال", image: Anzali },
    { name: "رامسر", subtitle: "اجاره ویلا", slug: "رامسر", image: Anzali },
    { name: "بندرانزلی", subtitle: "اجاره سوئیت در", slug: "تهران", image: Anzali },
    { name: "رودسر", subtitle: "اجاره سوئیت در", slug: "کیش", image: Anzali },
    { name: "صومعه سرا", subtitle: "رزرو بومگردی در", slug: "قشم", image: Anzali },
    { name: "لاهیجان", subtitle: "اجاره ویلا", slug: "رشت", image: Anzali },
    { name: "ماسال", subtitle: "اجاره ویلا", slug: "ماسال", image: Anzali },
    { name: "اسالم", subtitle: "اجاره کلبه در", slug: "اولسبلنگاه", image: Anzali },
    { name: "تالش", subtitle: "اجاره ویلا در", slug: "فیلبند", image: Anzali },
    { name: "ماسال", subtitle: "اجاره ویلا", slug: "ماسال", image: Anzali },
    { name: "اسالم", subtitle: "اجاره کلبه در", slug: "اولسبلنگاه", image: Anzali },
    { name: "تالش", subtitle: "اجاره ویلا در", slug: "فیلبند", image: Anzali },
];

const DestinationCard: React.FC<{ dest: Destination }> = ({ dest }) => (
    <Link
        to={`/places/city/${encodeURIComponent(dest.slug)}`}
        className="flex items-center gap-3 bg-white border-[1.8px] border-[#CFD6DC] rounded-xl px-3 py-2 md:px-4 md:py-3 group hover:border-gray-400 transition-colors duration-150 w-[170px] md:w-[240px] flex-shrink-0"
    >
        {/* Square image */}
        <div className="w-12 h-12 md:w-[72px] md:h-[72px] rounded-lg overflow-hidden flex-shrink-0">
            <img
                src={dest.image}
                alt={dest.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
        </div>

        {/* Text */}
        <div className="flex flex-col items-end flex-1 min-w-0">
            <span className="text-sm md:text-base font-myYekanDemibold text-gray-900 whitespace-nowrap">
                {dest.name}
            </span>
        </div>

        {/* Chevron */}
        <ChevronLeftIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
    </Link>
);

const PopularDestinations: React.FC = () => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const scrollPrev = () => {
        if (scrollRef.current) {
            const scrollAmount = scrollRef.current.clientWidth;
            scrollRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
        }
    };

    const scrollNext = () => {
        if (scrollRef.current) {
            const scrollAmount = scrollRef.current.clientWidth;
            scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
    };

    return (
        <div className="py-4 px-0 desktop:px-16">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 desktop:px-20 tablet:px-20 px-[16px]">
                <h2 className="text-xl tablet:text-2xl desktop:text-3xl font-myYekanDemibold">
                    مقاصد پرطرفدار
                </h2>
                <Link
                    to="/destinations"
                    className="flex items-center gap-1 text-sm text-gray-800 font-myYekanFaNumMedium"
                >
                    مشاهده همه
                    <ChevronLeftIcon className="h-4 w-4" />
                </Link>
            </div>

            {/* Desktop view with navigation buttons */}
            <div className="hidden md:block relative px-12">
                <div className="flex items-center">
                    <button 
                        onClick={scrollPrev} 
                        className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                    >
                        <ChevronRightIcon className="h-6 w-6" />
                    </button>
                    
                    <div
                        ref={scrollRef}
                        className="flex overflow-x-auto gap-2 md:gap-3 pb-4 flex-1 scrollbar-hide snap-mandatory snap-x"
                        style={{ 
                            WebkitOverflowScrolling: "touch",
                            scrollSnapType: "x mandatory"
                        }}
                    >
                        {destinations.map((dest, i) => (
                            <div key={i} className="snap-start">
                                <DestinationCard dest={dest} />
                            </div>
                        ))}
                    </div>
                    
                    <button 
                        onClick={scrollNext} 
                        className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                    >
                        <ChevronLeftIcon className="h-6 w-6" />
                    </button>
                </div>
            </div>

            {/* Mobile/Tablet view - simple horizontal scroll */}
            <div className="md:hidden">
                <div
                    className="flex overflow-x-auto gap-2 px-[16px] pb-4 scrollbar-hide"
                    style={{ WebkitOverflowScrolling: "touch" }}
                >
                    {destinations.map((dest, i) => (
                        <DestinationCard key={i} dest={dest} />
                    ))}
                </div>
            </div>

            <style>{`
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { scrollbar-width: none; }
            `}</style>
        </div>
    );
};

export default PopularDestinations;